import OpenAI from 'openai';
import { toast } from './toast';
import { marked } from 'marked';
import { supabaseClient } from './supabase';

export interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

// Keep track of active threads
const activeThreads = new Map<string, { id: string; lastUsed: number }>();

// Configure marked options
marked.setOptions({
  gfm: true,
  breaks: true
});

// Custom renderer for HTML output
const renderer = new marked.Renderer();

// Customize table rendering
renderer.table = (header: string, body: string) => {
  return `
    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          ${header}
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          ${body}
        </tbody>
      </table>
    </div>
  `;
};

// Customize heading rendering
renderer.heading = (text: string, level: number) => {
  const sizes = {
    1: 'text-2xl',
    2: 'text-xl',
    3: 'text-lg',
    4: 'text-base',
    5: 'text-sm',
    6: 'text-xs'
  };
  return `<h${level} class="font-bold ${sizes[level as keyof typeof sizes]} my-2">${text}</h${level}>`;
};

// Customize list rendering
renderer.list = (body: string, ordered: boolean) => {
  const type = ordered ? 'ol' : 'ul';
  return `<${type} class="list-${ordered ? 'decimal' : 'disc'} ml-4 my-2">${body}</${type}>`;
};

// Function to get OpenAI credentials from database
async function getOpenAICredentials(): Promise<{ apiKey: string; assistantId: string }> {
  const { data, error } = await supabaseClient
    .from('api_services')
    .select('api_key, config, is_active')
    .eq('service_type', 'openai')
    .single();

  if (error) {
    toast.error('Failed to fetch OpenAI credentials');
    throw new Error('Failed to fetch OpenAI credentials');
  }
  if (!data) {
    toast.error('OpenAI service not found');
    throw new Error('OpenAI service not found');
  }
  if (!data.is_active) {
    toast.error('OpenAI service is not active');
    throw new Error('OpenAI service is not active');
  }
  if (!data.config?.assistant_id) {
    toast.error('OpenAI assistant ID not configured');
    throw new Error('OpenAI assistant ID not configured');
  }

  return {
    apiKey: data.api_key,
    assistantId: data.config.assistant_id
  };
}

// Function to create OpenAI client with API key
async function createOpenAIClient(): Promise<{ client: OpenAI; assistantId: string }> {
  const { apiKey, assistantId } = await getOpenAICredentials();
  return {
    client: new OpenAI({
      apiKey,
      dangerouslyAllowBrowser: true
    }),
    assistantId
  };
}

export async function* streamAssistantResponse(messages: Message[], threadId?: string): AsyncGenerator<string, void, unknown> {
  try {
    // Create OpenAI client with API key and get assistant ID
    const { client: openai, assistantId } = await createOpenAIClient();

    let thread;

    if (threadId && activeThreads.has(threadId)) {
      thread = { id: activeThreads.get(threadId)!.id };
      activeThreads.set(threadId, { 
        id: thread.id,
        lastUsed: Date.now()
      });
    } else {
      thread = await openai.beta.threads.create({}, {
        headers: {
          'OpenAI-Beta': 'assistants=v2'
        }
      });
      
      if (threadId) {
        activeThreads.set(threadId, {
          id: thread.id,
          lastUsed: Date.now()
        });
      }
    }

    for (const message of messages) {
      await openai.beta.threads.messages.create(
        thread.id,
        {
          role: message.role === 'system' ? 'user' : message.role,
          content: message.content
        },
        {
          headers: {
            'OpenAI-Beta': 'assistants=v2'
          }
        }
      );
    }

    const run = await openai.beta.threads.runs.create(
      thread.id,
      {
        assistant_id: assistantId,
        instructions: `You are an expert SEO content analyst and copywriter. Your task is to analyze content and provide specific, actionable recommendations.

🔴 CRITICAL LANGUAGE RULE - MANDATORY:
- You MUST respond ONLY in the language specified in the user's message
- If language code is "uk" - respond ONLY in Ukrainian (українська мова)
- If language code is "ru" - respond ONLY in Russian (русский язык)  
- If language code is "en" - respond ONLY in English
- NO mixing of languages - use ONE language throughout the entire response
- This is NOT optional - language consistency is REQUIRED

When analyzing keywords:
- Focus on natural integration opportunities
- Suggest specific sentence structures or phrases in the TARGET LANGUAGE
- Consider semantic variations and context
- Recommend keyword placement strategies

When analyzing content:
- Evaluate content structure and flow
- Identify gaps in topic coverage based on competitor analysis
- Suggest improvements for readability and SEO
- Provide specific examples in the TARGET LANGUAGE
- Consider search intent (commercial, informational, navigational, transactional)
- Adapt recommendations to content type (blog, category page, product page, etc.)

Content Type Specific Guidelines:
- For COMMERCIAL content (e-commerce categories/products): Focus on conversion, product benefits, CTAs, trust signals
- For BLOG-BEST lists: Structure numbered lists, comparison tables, objective criteria
- For BLOG-HOW-TO: Step-by-step instructions, clear headings, actionable tips
- For BLOG-GUIDE: Comprehensive coverage, table of contents, detailed sections
- For INFORMATIONAL content: Answer user questions, provide value, build authority

Format your responses using Markdown for better readability:
- Use headers (# ## ###) for sections
- Use bullet points and numbered lists
- Use tables when comparing data
- Use bold and italic for emphasis
- Use code blocks for examples

Maintain conversation context and refer to previous messages when appropriate.
REMEMBER: Always respond in the same language as the content being analyzed!`
      },
      {
        headers: {
          'OpenAI-Beta': 'assistants=v2'
        }
      }
    );

    while (true) {
      const runStatus = await openai.beta.threads.runs.retrieve(
        thread.id,
        run.id,
        {
          headers: {
            'OpenAI-Beta': 'assistants=v2'
          }
        }
      );

      if (runStatus.status === 'completed') {
        const messages = await openai.beta.threads.messages.list(
          thread.id,
          {
            headers: {
              'OpenAI-Beta': 'assistants=v2'
            }
          }
        );
        
        const latestMessage = messages.data
          .filter(msg => msg.role === 'assistant')
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];

        if (latestMessage && latestMessage.content[0].type === 'text') {
          const content = latestMessage.content[0].text.value;
          yield content;
        }
        break;
      } else if (runStatus.status === 'failed') {
        throw new Error('Assistant run failed');
      }

      await new Promise(resolve => setTimeout(resolve, 1000));
    }

  } catch (error) {
    console.error('Error in streamAssistantResponse:', error);
    toast.error(error instanceof Error ? error.message : 'Failed to get assistant response');
    throw error;
  }
}

export async function generateAssistantResponse(messages: Message[], threadId?: string): Promise<string> {
  let fullResponse = '';
  
  try {
    for await (const chunk of streamAssistantResponse(messages, threadId)) {
      fullResponse += chunk;
    }
    
    // Convert markdown to HTML using marked with custom renderer
    const htmlContent = await marked(fullResponse, { renderer });

    // Add additional styling classes
    return htmlContent
      .replace(/<table>/g, '<table class="min-w-full divide-y divide-gray-200">')
      .replace(/<thead>/g, '<thead class="bg-gray-50">')
      .replace(/<tbody>/g, '<tbody class="bg-white divide-y divide-gray-200">')
      .replace(/<th>/g, '<th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">')
      .replace(/<td>/g, '<td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">');

  } catch (error) {
    console.error('Error generating assistant response:', error);
    toast.error(error instanceof Error ? error.message : 'Failed to generate response');
    throw error;
  }
}

// Clean up threads after a period of inactivity
setInterval(async () => {
  const now = Date.now();
  for (const [threadId, threadInfo] of activeThreads.entries()) {
    if (now - threadInfo.lastUsed > 30 * 60 * 1000) { // 30 minutes
      try {
        const { client: openai } = await createOpenAIClient();
        await openai.beta.threads.del(threadInfo.id, {
          headers: {
            'OpenAI-Beta': 'assistants=v2'
          }
        });
        activeThreads.delete(threadId);
      } catch (error) {
        console.error('Error cleaning up thread:', error);
      }
    }
  }
}, 5 * 60 * 1000); // Check every 5 minutes